import { useBlocksConfig, useSyncer } from "@pathwright/blocks-engine"
import { MediaManager } from "@pathwright/legacy-media-manager"
import BackgroundImage from "@pathwright/ui/src/components/background/BackgroundImage"
import Button from "@pathwright/ui/src/components/button/Button"
import Image from "@pathwright/ui/src/components/image/Image"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import PropTypes from "prop-types"
import React from "react"
import styled, { css } from "styled-components"
import Annotation from "./annotation/Annotation"
import Caption, { CaptionText } from "./caption/Caption"
import CaptionEdit from "./caption/CaptionEdit"

const ImageWrapper = styled.div`
  width: 100%;
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: ${(props) =>
    props.annotationsEnabled && !props.readOnly ? "crosshair" : "auto"};

  ${(props) =>
    props.backgroundMode &&
    css`
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    `};

  img {
    width: 100%;
    user-select: none;
    /* box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
    border-radius: 5px; */
  }

  .BackgroundImage {
    pointer-events: none;
  }

  html.no-touch & .Blocks__ImageControls {
    opacity: 0;
  }

  html.no-touch &:hover .Blocks__ImageControls {
    opacity: 1;
  }

  ${(p) =>
    p.annotationsEnabled
      ? `
    .Blocks__ImageControls {
      pointer-events: none;

      .Button {
        pointer-events: all;
      }
    }
  `
      : ""}
`

const ImageControls = styled.div`
  z-index: 10;
  position: absolute;
  left: 0px;
  top: 0px;
  padding: 0px 10px;
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 300ms;
`

const imageEditDefaultPropImagePickerOptions = {}

// handles everything you can do with an image
const ImageEdit = (props) => {
  props = {
    imagePickerOptions: imageEditDefaultPropImagePickerOptions,
    atMaxAnnotations: false,
    ...props
  }

  const {
    image,
    altText,
    overlay,
    caption,
    annotations,
    atMaxAnnotations,
    onChangeImage,
    onChangeCaption,
    onChangeAnnotations,
    readOnly,
    annotationsOnly,
    captionEnabled,
    captionEditOnly,
    captionBounds,
    annotationsEnabled,
    imagePickerOptions,
    renderButton,
    backgroundMode,
    className,
    imageStyle,
    blockStyles
  } = props

  const containerRef = React.useRef(null)

  const { contextKey, userID, accountID } = useBlocksConfig()
  const { blocksContext } = useSyncer()
  const mediaStoragePath = blocksContext?.mediaStoragePath

  const [activeAnnotationIndex, setActiveAnnotationIndex] = React.useState(null)
  const [captionNeedsFocus, setCaptionNeedsFocus] = React.useState(false)

  const activeAnnotationIndexRef = React.useRef(null)
  activeAnnotationIndexRef.current = activeAnnotationIndex

  const getAnnotations = () => annotations || []

  const renderAnnotations = () =>
    getAnnotations().map((annotation, i) => (
      <Annotation
        key={i}
        annotation={annotation}
        build={!readOnly || annotationsOnly}
        active={activeAnnotationIndex === i}
        onShowAnnotation={() => setActiveAnnotationIndex(i)}
        onHideAnnotation={(reason) => {
          if (activeAnnotationIndexRef.current === i) {
            setActiveAnnotationIndex(null)
          }
        }}
        onChangeAnnotation={(annotation) =>
          handleChangeAnnotation(i, annotation)
        }
        onRemoveAnnotation={() => handleRemoveAnnotation(i)}
      />
    ))

  const renderCaption = () => {
    if (readOnly && !captionEditOnly) {
      if (caption) {
        return <Caption caption={caption} />
      }

      return null
    }

    if (caption) {
      return (
        <CaptionEdit
          onChange={handleChangeCaption}
          caption={caption}
          autoFocus={captionNeedsFocus}
          bounds={captionBounds}
        />
      )
    }

    return (
      <CaptionText style={{ cursor: "pointer" }} onClick={handleAddCaption}>
        Click to add caption
      </CaptionText>
    )
  }

  const handleClickImage = (e) => {
    // Abort if an annotation is already active.
    if (activeAnnotationIndex !== null) {
      return
    }

    if (
      (!readOnly || annotationsOnly) &&
      annotationsEnabled &&
      activeAnnotationIndex == null &&
      !atMaxAnnotations
    ) {
      const nextIndex = getAnnotations().length
      setActiveAnnotationIndex(nextIndex)
      handleAddAnnotation(e)
    }
  }

  function focusNextAnnotation(nextAnnotations) {
    setActiveAnnotationIndex(
      nextAnnotations.length ? nextAnnotations.length - 1 : null
    )
  }

  const handleAddAnnotation = (e) => {
    const dim = e.target.getBoundingClientRect()
    const ne = e.nativeEvent
    const xPos = ((ne.x - dim.left) / dim.width) * 100
    const yPos = ((ne.y - dim.top) / dim.height) * 100
    const nextAnnotations = getAnnotations().concat({ xPos, yPos, html: "" })
    focusNextAnnotation(nextAnnotations)
    onChangeAnnotations(nextAnnotations)
  }

  const handleChangeAnnotation = (index, data) => {
    const nextAnnotations = getAnnotations().map((annotation, i) => {
      if (i === index) return { ...annotation, ...data }
      return annotation
    })
    onChangeAnnotations(nextAnnotations)
  }

  const handleRemoveAnnotation = (index) => {
    const nextAnnotations = getAnnotations().filter(
      (annotation, i) => i !== index
    )
    focusNextAnnotation(nextAnnotations)
    onChangeAnnotations(nextAnnotations)
  }

  const handleChangeImage = async (data) => {
    onChangeImage(data.url, data.description)
  }

  const handleAddCaption = () => {
    setCaptionNeedsFocus(true)
    onChangeCaption({ html: "" })
  }

  const handleChangeCaption = (caption) => {
    if (captionNeedsFocus) {
      setCaptionNeedsFocus(false)
    }
    onChangeCaption(caption)
  }

  const style = {
    marginTop:
      blockStyles?.selectedSettings.blockHeight === "vertspacingfull"
        ? "20px"
        : ""
  }

  const renderImageButton = ({ onClick }) => (
    <ImageControls className="Blocks__ImageControls">
      {renderButton ? (
        renderButton({ onClick })
      ) : (
        <Button
          styleType="secondary"
          inverted
          size="medium"
          onClick={onClick}
          className="EditButton"
          style={style}
        >
          <Pathicon icon="shuffle" style={{ marginRight: "0.5rem" }} />
          Replace Image
        </Button>
      )}
    </ImageControls>
  )

  return (
    <div ref={containerRef}>
      <ImageWrapper
        annotationsEnabled={annotationsEnabled}
        backgroundMode={backgroundMode}
        className={`BlockImage ${className || ""}`}
      >
        {!readOnly ? (
          <MediaManager
            contextKey={contextKey}
            documentScope={blocksContext?.documentScope}
            mediaStoragePath={mediaStoragePath}
            mediaType="image"
            onChooseMedia={handleChangeImage}
            userID={userID}
            accountID={accountID}
            renderButton={renderImageButton}
            buttonText="Replace Image"
            pickOptions={imagePickerOptions}
          />
        ) : null}
        {backgroundMode ? (
          <BackgroundImage image={image} overlay={overlay} altText={altText} />
        ) : (
          <Image
            alt={altText || "Image"}
            src={image}
            onClick={handleClickImage}
            style={imageStyle}
          />
        )}
        {annotationsEnabled && renderAnnotations()}
      </ImageWrapper>
      {captionEnabled && renderCaption()}
    </div>
  )
}

ImageEdit.propTypes = {
  // data
  image: PropTypes.string.isRequired,
  altText: PropTypes.string,
  overlay: PropTypes.string,
  caption: PropTypes.shape({
    html: PropTypes.string
  }),
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      xPos: PropTypes.number.isRequired,
      yPos: PropTypes.number.isRequired,
      html: PropTypes.string
    })
  ),
  atMaxAnnotations: PropTypes.bool,
  // -- handlers --
  onChangeImage: PropTypes.func,
  onChangeCaption: PropTypes.func,
  onChangeAnnotations: PropTypes.func,
  // -- functionality --
  // readOnly === "view" mode (Learner/Preview)
  readOnly: PropTypes.bool,
  backgroundMode: PropTypes.bool,
  // allows annotation assessment to ignore other ImageEdit features
  annotationsOnly: PropTypes.bool,
  captionEnabled: PropTypes.bool,
  // allows captions to be added even if in readOnly mode
  captionEditOnly: PropTypes.bool,
  // handed off to QuillEditor
  captionBounds: PropTypes.string,
  annotationsEnabled: PropTypes.bool,
  imagePickerOptions: PropTypes.object,
  // -- ui --
  renderButton: PropTypes.func,
  className: PropTypes.string,
  imageStyle: PropTypes.object,
  blockStyles: PropTypes.object
}

export default ImageEdit
